import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

// 扩展 dayjs 插件
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * 时区处理工具函数
 * 支持两种模式：UTC存储 或 本地时区存储
 */

// 配置
const TIMEZONE_CONFIG = {
  // 服务器时区（可根据实际部署环境调整）
  serverTimezone: 'Asia/Shanghai',
  // 存储模式：'utc' 或 'local'
  storageMode: 'local', // 使用本地时区存储（与后端配置一致）
  // 显示时区
  displayTimezone: 'Asia/Shanghai'
};

/**
 * 格式化时间显示
 * @param {string|Date} time - 时间
 * @param {string} format - 格式化字符串
 * @param {string} timezone - 显示时区
 * @returns {string} 格式化后的时间字符串
 */
export function formatTime(time, format = 'YYYY-MM-DD HH:mm:ss', timezone = TIMEZONE_CONFIG.displayTimezone) {
  if (!time) return '';
  return dayjs(time).tz(timezone).format(format);
}

/**
 * 获取当前时间（服务器时区）
 * @returns {dayjs.Dayjs} 当前时间
 */
export function getCurrentTime() {
  try {
    // 获取服务器时区的当前时间
    const now = dayjs().tz(TIMEZONE_CONFIG.serverTimezone);
    if (now.isValid()) {
      return now;
    } else {
      console.error('Failed to create valid current time');
      return dayjs().tz(TIMEZONE_CONFIG.serverTimezone);
    }
  } catch (error) {
    console.error('Error getting current time:', error);
    return dayjs().tz(TIMEZONE_CONFIG.serverTimezone);
  }
}

/**
 * 将时间转换为存储格式
 * @param {string|Date|dayjs.Dayjs} time - 时间
 * @returns {string} 存储格式的时间字符串
 */
export function toStorageFormat(time) {
  if (!time) return getCurrentTime().toISOString();
  
  const dayjsObj = dayjs(time);
  if (!dayjsObj.isValid()) {
    console.warn('Invalid time provided to toStorageFormat:', time);
    return getCurrentTime().toISOString();
  }
  
  if (TIMEZONE_CONFIG.storageMode === 'utc') {
    // UTC 存储模式：转换为 UTC 时间
    return dayjsObj.utc().toISOString();
  } else {
    // 本地时区存储模式：保持服务器时区
    return dayjsObj.tz(TIMEZONE_CONFIG.serverTimezone).format('YYYY-MM-DD HH:mm:ss');
  }
}

/**
 * 将时间转换为 ISO 字符串（兼容旧接口）
 * @param {string|Date|dayjs.Dayjs} time - 时间
 * @returns {string} ISO 格式的时间字符串
 */
export function toISOString(time) {
  return toStorageFormat(time);
}

/**
 * 创建时间对象
 * @param {string|Date|dayjs.Dayjs} time - 时间
 * @returns {dayjs.Dayjs} dayjs 对象
 */
export function createTime(time) {
  if (!time) return getCurrentTime();
  
  try {
    const dayjsObj = dayjs(time);
    if (dayjsObj.isValid()) {
      return dayjsObj;
    } else {
      console.warn('Invalid date provided to createTime:', time);
      return getCurrentTime();
    }
  } catch (error) {
    console.error('Error creating dayjs object:', error);
    return getCurrentTime();
  }
}

/**
 * 从存储格式解析时间
 * @param {string} storedTime - 存储的时间字符串
 * @returns {dayjs.Dayjs} 解析后的时间对象
 */
export function parseStoredTime(storedTime) {
  if (!storedTime) return getCurrentTime();
  
  try {
    if (TIMEZONE_CONFIG.storageMode === 'utc') {
      // UTC 存储：解析为 UTC 时间
      return dayjs.utc(storedTime);
    } else {
      // 本地时区存储：解析为服务器时区
      return dayjs.tz(storedTime, TIMEZONE_CONFIG.serverTimezone);
    }
  } catch (error) {
    console.error('Error parsing stored time:', storedTime, error);
    return getCurrentTime();
  }
}

/**
 * 获取服务器时间信息
 * @returns {object} 服务器时间信息
 */
export function getServerTimeInfo() {
  const now = getCurrentTime();
  return {
    serverTime: now.format('YYYY-MM-DD HH:mm:ss'),
    serverTimezone: TIMEZONE_CONFIG.serverTimezone,
    storageMode: TIMEZONE_CONFIG.storageMode,
    utcTime: now.utc().format('YYYY-MM-DD HH:mm:ss'),
    timestamp: now.valueOf()
  };
}

/**
 * 设置时区配置
 * @param {object} config - 时区配置
 */
export function setTimezoneConfig(config) {
  Object.assign(TIMEZONE_CONFIG, config);
}

export default {
  formatTime,
  getCurrentTime,
  toISOString,
  toStorageFormat,
  createTime,
  parseStoredTime,
  getServerTimeInfo,
  setTimezoneConfig
};
